import IdealStudio from "../core/editor/main";
import { triggerAuthRequired, setAuthRecoveryHandler } from "../_editor/utils/authRequired";
// Read once at boot: the URL may be rewritten later (token/code cleanup).
export const assetModeToken =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("assetMode") === "token";
export const STORAGE_KEYS = {
  TOKEN: "_requestVerificationToken",
};

// Guard against the 401 -> login -> 401 redirect loop. Redirects more than
// AUTH_REDIRECT_WINDOW_MS apart reset the counter; once more than
// AUTH_REDIRECT_MAX land within the window we stop redirecting.
const AUTH_REDIRECT_GUARD_KEY = "_authRedirectGuard";
const AUTH_REDIRECT_MAX = 3;
const AUTH_REDIRECT_WINDOW_MS = 30 * 1000;

// Returns false when we appear to be stuck in an auth redirect loop.
const registerAuthRedirect = () => {
  let info = null;
  try {
    info = JSON.parse(sessionStorage.getItem(AUTH_REDIRECT_GUARD_KEY));
  } catch (e) {
    info = null;
  }

  const now = Date.now();
  if (!info || typeof info.lastAt !== "number" || now - info.lastAt > AUTH_REDIRECT_WINDOW_MS) {
    info = { count: 0, firstAt: now, lastAt: now };
  }

  info.count += 1;
  info.lastAt = now;

  try {
    sessionStorage.setItem(AUTH_REDIRECT_GUARD_KEY, JSON.stringify(info));
  } catch (e) {
    return true; // sessionStorage unavailable — fail open
  }

  return info.count <= AUTH_REDIRECT_MAX;
};

export const clearAuthRedirectGuard = () => {
  try {
    sessionStorage.removeItem(AUTH_REDIRECT_GUARD_KEY);
  } catch (e) {
    /* ignore */
  }
};

// A manual refresh resets the counter; the automatic 401 bounce is a "navigate",
// not a "reload", so it keeps accumulating.
const wasPageReloaded = () => {
  try {
    const navs =
      typeof performance !== "undefined" &&
      performance.getEntriesByType &&
      performance.getEntriesByType("navigation");
    if (navs && navs.length) return navs[0].type === "reload";
    return (
      typeof performance !== "undefined" &&
      performance.navigation &&
      performance.navigation.type === 1
    );
  } catch (e) {
    return false;
  }
};

if (typeof window !== "undefined" && wasPageReloaded()) {
  clearAuthRedirectGuard();
}

let reduxStore = null;

export const setStore = (store) => {
  reduxStore = store;
};

export const getStore = () => {
  return reduxStore;
};

const saveToLocalStorage = (key, value) => {
  if (value !== null && value !== undefined && value !== "") {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn("Failed to save to localStorage:", key, e);
    }
  }
};

const getFromLocalStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn("Failed to read from localStorage:", key, e);
    return null;
  }
};

export const clearStoredEnvironment = () => {
  Object.values(STORAGE_KEYS).forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn("Failed to remove from localStorage:", key, e);
    }
  });
  // Also clear window properties
  delete window.idealStudioTrainingId;
  delete window._requestVerificationToken;
  delete window.tenantId;
  delete window.ideaLibraryRootUrl;
  clearRefreshToken();
};

export const getToken = () => {
  const stored = getFromLocalStorage(STORAGE_KEYS.TOKEN);
  if (stored) return stored;

  // public/index_visor.html (public preview + the video-export recorder) gets
  // its token as a `?token=` URL param and publishes it on window under the
  // SAME name as the localStorage key (STORAGE_KEYS.TOKEN ===
  // "_requestVerificationToken") — but it never writes localStorage. Without
  // this fallback getToken() returns empty there, so EVERY call the visor
  // bundle makes through server_utils/http_service goes out with
  // `Authorization: Bearer undefined`.
  //
  // That page renders anyway because it fetches the training state with its own
  // fetch + explicit header, bypassing server_utils — which is why the symptom
  // is silent and partial: the scene appears, but bundle-initiated calls 401.
  // Concretely, fetchSubtitleLanguages() failing leaves
  // availableSubtitleLanguages empty, and VisorSubtitleManager
  // .applySubtitleFromUrlParamIfNeeded() then returns early forever — so
  // `enableSubtitle=<lang>` is accepted and never applied, and the exported
  // video has no subtitles.
  if (typeof window !== "undefined" && window[STORAGE_KEYS.TOKEN]) {
    return window[STORAGE_KEYS.TOKEN];
  }

  return stored;
};

const decodeJWT = (token) => {
  try {
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
};

export const getUserId = () => decodeJWT(getToken())?.sub || null;

export const getOrganizationId = () => {
  const token = getToken();
  const decodedToken = decodeJWT(token);
  return decodedToken?.organizationId || window.idealStudioOrganizationId || null;
};

export const getUserInfo = () => {
  const decoded = decodeJWT(getToken()) || {};
  const name =
    decoded.username ||
    decoded.preferred_username ||
    decoded.name ||
    decoded.userName ||
    decoded.email ||
    decoded.sub ||
    "";
  const email = decoded.email || decoded.mail || decoded.username || "";
  return { name, email };
};

const setToken = (value) => {
  window._requestVerificationToken = value;
  if (value) {
    saveToLocalStorage(STORAGE_KEYS.TOKEN, value);
  }
};

// Cookie TTL is 72h server-side (studio-files.controller.ts `assetGrant`); a
// 5-min renewal keeps it fresh. Event-driven re-grants (tab visible, back
// online, token refreshed) are throttled to one per 30s.
const ASSET_GRANT_REFRESH_MS = 5 * 60 * 1000;
const ASSET_GRANT_MIN_GAP_MS = 30 * 1000;
let assetGrantRefreshStarted = false;
let lastAssetGrantAt = 0;
// The access token whose session turned out to be over (refresh rejected or
// no refresh token). The timer keeps ticking but sends nothing with it; a new
// token (refresh, another tab, re-login) differs and resumes the grants.
// Without this an abandoned tab sent a doomed 401 every 5 min for hours.
let deadAssetGrantToken = null;

const tokenIsExpired = (token) => {
  const payload = decodeJWT(token);
  return !!payload && typeof payload.exp === "number" && payload.exp * 1000 <= Date.now();
};

const sendAssetGrant = (base, token) => {
  // trainingId scopes the minted cookie to that training's organization —
  // needed when the current user's own org differs from the training's
  // (e.g. an admin opening another org's training). Optional server-side.
  const trainingId = window.idealStudioTrainingId;
  const url =
    base +
    "api/studio-files/asset-grant" +
    (trainingId ? "?trainingId=" + encodeURIComponent(trainingId) : "");
  return fetch(url, {
    credentials: "include",
    headers: { Authorization: "Bearer " + token },
  });
};

// Renews the access token for the grant. Never opens the sign-in modal: this
// is a background call, and the user's next real request handles a dead
// session. Resolves true when there is a fresh token to grant with.
const renewTokenForAssetGrant = async (token) => {
  const status = await refreshWithStatus();
  if (status === "rejected" || status === "none") deadAssetGrantToken = token;
  return status === "ok";
};

// Mints the `aidea_asset` cookie the private blob route (GET
// /api/studio-files/blob/*path) accepts as a cookie-mode auth path — needed
// because cross-origin fetch() doesn't send cookies without credentials:
// 'include', and the cookie only exists once this grant call succeeds. Fails
// soft: a rejected/failed grant just means asset URLs fall back to `?t=`
// token mode, not a broken editor load.
//
// An expired token is renewed BEFORE granting, and a 401 gets one silent
// refresh + retry — the same recovery the other mw calls get.
const requestAssetGrant = async () => {
  const base = window.ideaLibraryRootUrl;
  let token = getToken();
  if (!base || !token || token === deadAssetGrantToken) return;
  // Set before any await: the successful refresh below re-grants through the
  // throttled path, which this keeps quiet so the grant goes out once.
  lastAssetGrantAt = Date.now();
  try {
    if (tokenIsExpired(token)) {
      if (!(await renewTokenForAssetGrant(token))) return;
      token = getToken();
    }
    let response = await sendAssetGrant(base, token);
    if (response.status === 401) {
      if (!(await renewTokenForAssetGrant(token))) return;
      token = getToken();
      response = await sendAssetGrant(base, token);
      // A fresh token still refused: stop retrying with it.
      if (response.status === 401) deadAssetGrantToken = token;
    }
    if (!response.ok) {
      console.warn("Asset grant request failed with status", response.status);
    }
  } catch (e) {
    console.warn("Asset grant request failed:", e);
  }
};

// Capped so a slow or failing grant endpoint never hangs boot.
export const assetGrantReady = (timeoutMs = 3000) => {
  let timer;
  const timeout = new Promise((resolve) => (timer = setTimeout(resolve, timeoutMs)));
  return Promise.race([firstAssetGrant, timeout]).finally(() => clearTimeout(timer));
};

const requestAssetGrantThrottled = () => {
  if (Date.now() - lastAssetGrantAt < ASSET_GRANT_MIN_GAP_MS) return;
  requestAssetGrant();
};

// Resolves once the first grant settled (never rejects). The editor awaits it
// before mounting so boot-time /api/f/ requests carry the cookie.
let firstAssetGrant = Promise.resolve();

const startAssetGrantRefresh = () => {
  // assetMode=token: cookies may be unavailable (embedded); assets use `?t=`.
  if (assetModeToken) return;
  firstAssetGrant = requestAssetGrant();
  if (assetGrantRefreshStarted) return;
  assetGrantRefreshStarted = true;
  window.setInterval(requestAssetGrant, ASSET_GRANT_REFRESH_MS);
  // Editor only: the visor authenticates with share/recorder tokens.
  if (window.location.pathname.includes("index_visor.html")) return;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") requestAssetGrantThrottled();
  });
  window.addEventListener("online", requestAssetGrantThrottled);
};

// ── Access-token refresh ────────────────────────────────────────────────────
// mw access tokens are short-lived (ACCESS_TOKEN_TTL, 1h by default) and the
// session is carried by a separate refresh token (POST /auth/refresh, 72h).
// Without renewing, the editor dies with "You need to sign in again" as soon
// as the handed-off token expires — often a few minutes in, because console
// hands over whatever token its cookie holds, not a fresh one.
//
// The refresh token lives in localStorage (like the access token) so a closed
// tab / reload keeps the session; tabs share it, and the sub+sid match check
// below drops it if the access token belongs to another session.
const REFRESH_TOKEN_KEY = "_refreshToken";
// Refresh at 80% of the token's lifetime: mw's idle timeout (IDLE_TIMEOUT,
// 8h by default) is measured from the last login/refresh, so waiting until
// just before the token expires would race it when the two are set close.
const REFRESH_AT_LIFETIME_RATIO = 0.8;
const REFRESH_MIN_LEAD_MS = 60 * 1000;
// Transient failures (network down, 5xx) back off from 30s up to 5 min and
// keep retrying for as long as the refresh token lives — only a 401 ends the
// session. Giving up once the access token had expired left a tab that slept
// through its expiry with no refresh loop at all.
const REFRESH_RETRY_MS = 30 * 1000;
const REFRESH_RETRY_MAX_MS = 5 * 60 * 1000;
// A 403 is not always an expired session — don't let a genuinely forbidden
// call trigger a refresh storm. One silent recovery per window.
const RECOVERY_COOLDOWN_MS = 30 * 1000;
// A 401 recovery that hits a transient failure retries quickly before giving
// up: right after a tab resumes or the OS wakes, the first request often
// fails (ERR_NETWORK_IO_SUSPENDED) although the session is perfectly valid.
const RECOVERY_RETRY_DELAYS_MS = [1000, 3000, 5000];

const getRefreshToken = () => {
  try {
    const stored = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (stored) return stored;
    // One-time migration from the old per-tab sessionStorage location.
    const legacy = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (legacy) {
      localStorage.setItem(REFRESH_TOKEN_KEY, legacy);
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    return legacy;
  } catch (e) {
    return null;
  }
};

const setRefreshToken = (value) => {
  try {
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    if (value) localStorage.setItem(REFRESH_TOKEN_KEY, value);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (e) {
    console.warn("Failed to store refresh token:", e);
  }
};

const clearRefreshToken = () => setRefreshToken(null);

const getMwBaseUrl = () => {
  let base = window.ideaLibraryRootUrl || process.env.MW_API_URL || "";
  if (base && !base.toLowerCase().startsWith("http")) {
    base = window.location.origin + base;
  }
  return base.replace(/\/+$/, "");
};

let refreshTimer = null;
let refreshInFlight = null;
let lastRecoveryAt = 0;
let refreshRetryMs = REFRESH_RETRY_MS;

// Resolves "ok", "rejected" (401: the session is over), "transient" (network
// error / 5xx: worth retrying) or "none" (no refresh token to use).
const doRefreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return "none";
  try {
    const response = await fetch(`${getMwBaseUrl()}/api/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!response.ok) {
      // 401 = refresh token expired/revoked/idle-timed-out: the session is
      // over, stop trying. Anything else (5xx) may be transient — keep it.
      if (response.status === 401) {
        clearRefreshToken();
        return "rejected";
      }
      return "transient";
    }
    const data = await response.json();
    if (!data || typeof data.access_token !== "string") return "transient";
    setToken(data.access_token);
    refreshRetryMs = REFRESH_RETRY_MS;
    scheduleTokenRefresh();
    requestAssetGrantThrottled();
    return "ok";
  } catch (e) {
    console.warn("Access token refresh failed:", e);
    return "transient";
  }
};

// Single-flight: the timer, a resume event and a burst of 401s can all ask at
// the same moment.
const refreshWithStatus = () => {
  if (!refreshInFlight) {
    refreshInFlight = doRefreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

export const refreshAccessToken = () => refreshWithStatus().then((status) => status === "ok");

function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

async function runScheduledRefresh() {
  refreshTimer = null;
  const status = await refreshWithStatus();
  if (status === "transient" && getRefreshToken()) scheduleRefreshRetry();
}

function scheduleRefreshRetry() {
  clearRefreshTimer();
  const delay = refreshRetryMs;
  refreshRetryMs = Math.min(refreshRetryMs * 2, REFRESH_RETRY_MAX_MS);
  refreshTimer = setTimeout(runScheduledRefresh, delay);
}

function scheduleTokenRefresh() {
  clearRefreshTimer();
  if (!getRefreshToken()) return;
  const payload = decodeJWT(getToken());
  if (!payload || typeof payload.exp !== "number") return;

  const now = Date.now();
  const expMs = payload.exp * 1000;
  const iatMs = typeof payload.iat === "number" ? payload.iat * 1000 : now;
  const refreshAt = Math.min(
    iatMs + (expMs - iatMs) * REFRESH_AT_LIFETIME_RATIO,
    expMs - REFRESH_MIN_LEAD_MS
  );

  refreshTimer = setTimeout(runScheduledRefresh, Math.max(refreshAt - now, 0));
}

const accessTokenIsLive = () => {
  const payload = decodeJWT(getToken());
  return !!payload && typeof payload.exp === "number" && payload.exp * 1000 > Date.now();
};

// Background-tab freezing and OS sleep stop timers outright, so the scheduled
// refresh may never have run. Re-check whenever the tab can act again instead
// of waiting for the first 401.
const refreshIfStale = () => {
  if (!getRefreshToken()) return;
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  const payload = decodeJWT(getToken());
  if (!payload || typeof payload.exp !== "number") return;
  if (payload.exp * 1000 - Date.now() > REFRESH_MIN_LEAD_MS) return;
  clearRefreshTimer();
  runScheduledRefresh();
};

let resumeListenersAdded = false;
const addResumeListeners = () => {
  if (resumeListenersAdded || typeof window === "undefined") return;
  resumeListenersAdded = true;
  document.addEventListener("visibilitychange", refreshIfStale);
  window.addEventListener("focus", refreshIfStale);
  window.addEventListener("online", refreshIfStale);
  window.addEventListener("pageshow", refreshIfStale);
};

// Legacy `?token=` hand-offs (and hosts calling initializeEnvironment with a
// token) arrive without a refresh token. While the access token is still
// valid, trade it for a full pair through the same session-code exchange the
// `?code=` hand-off uses — this also replaces a nearly-expired token with a
// fresh one. Bound to the same sub+sid server-side.
const obtainRefreshTokenFromAccessToken = async () => {
  const token = getToken();
  if (!token) return false;
  const base = getMwBaseUrl();
  try {
    const response = await fetch(`${base}/api/auth/session-code`, {
      method: "POST",
      headers: { Authorization: "Bearer " + token },
    });
    if (!response.ok) return false;
    const data = await response.json();
    if (!data || typeof data.code !== "string") return false;
    const pair = await exchangeSessionCode(data.code, base);
    if (!pair) return false;
    setToken(pair.access_token);
    setRefreshToken(pair.refresh_token);
    return true;
  } catch (e) {
    console.warn("Could not obtain a refresh token:", e);
    return false;
  }
};

// A refresh token only belongs to the access token it was issued with. If the
// host swapped the access token (another user or session), drop the old one.
const refreshTokenMatchesAccessToken = () => {
  const access = decodeJWT(getToken());
  const refresh = decodeJWT(getRefreshToken());
  return !!access && !!refresh && access.sub === refresh.sub && access.sid === refresh.sid;
};

const startTokenRefresh = async () => {
  // Visor pages authenticate with share/recorder tokens, not user sessions.
  if (window.location.pathname.includes("index_visor.html")) return;
  if (!getToken()) return;
  addResumeListeners();
  if (getRefreshToken() && !refreshTokenMatchesAccessToken()) {
    clearRefreshToken();
  }
  if (!getRefreshToken()) {
    await obtainRefreshTokenFromAccessToken();
  }
  scheduleTokenRefresh();
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Resolves true when no sign-in is needed (renewed, or a stale 401 to ignore),
// "transient" when mw could not be reached — a connectivity problem, not an
// expired session, so no sign-in modal; the background retry renews the token
// once mw is back — and false only when the session is really over.
const recoverSession = async () => {
  if (!getRefreshToken()) return false;
  if (Date.now() - lastRecoveryAt < RECOVERY_COOLDOWN_MS) {
    // Just renewed: a 401 now was almost always sent with the old token before
    // the renewal landed. Holding a live token means there is nothing to log
    // back into.
    return accessTokenIsLive();
  }
  let status = await refreshWithStatus();
  for (const delay of RECOVERY_RETRY_DELAYS_MS) {
    if (status !== "transient") break;
    await sleep(delay);
    status = await refreshWithStatus();
  }
  if (status === "ok") {
    lastRecoveryAt = Date.now();
    return true;
  }
  if (status === "transient") {
    scheduleRefreshRetry();
    return "transient";
  }
  return false;
};

setAuthRecoveryHandler(recoverSession);

// Another tab refreshed (or logged out): adopt its token and re-time our
// refresh instead of double-refreshing. A sleeping laptop freezes timers, so
// re-time on wake-up too (overdue refreshes fire immediately).
if (typeof window !== "undefined" && !window.location.pathname.includes("index_visor.html")) {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEYS.TOKEN && e.newValue) {
      window._requestVerificationToken = e.newValue;
      scheduleTokenRefresh();
    } else if (e.key === REFRESH_TOKEN_KEY) {
      scheduleTokenRefresh();
    }
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleTokenRefresh();
  });
}

export const initializeEnvironment = (config = {}) => {
  const {
    idealStudioTrainingId = null,
    ideaLibraryRootUrl = process.env.MW_API_URL,
    /* ideaLibraryRootUrl = "http://localhost:4000/", */
    tenantId = null,
    userLoginInfo = null,
    enableWithAI = true,
    // B1: agentic-chat panel. The panel is now ALWAYS enabled in every
    // environment — EditorApp renders it unconditionally (no flag, no
    // kill-switch). This field is kept for backward-compat (hosts may still pass
    // it; it sets window.enableAIChatPopup) but it NO LONGER gates rendering.
    enableAIChatPopup = true,
    enableTextToImageOpenAi = true,
    enableTextTospeechOpenAi = true,
    enableTextTospeechMicrosoft = true,
    aiVoiceServiceType = -1,
    aiVoiceName = "",
    requestVerificationToken = null,
  } = config;

  let finalToken = requestVerificationToken;
  if (!finalToken) {
    finalToken = getToken();
  }

  setToken(finalToken);

  window.idealStudio_editor_params = {};

  window.idealStudio_editor_params.export_url = IdealStudio.Config.export_url;
  if (idealStudioTrainingId !== null && idealStudioTrainingId !== undefined) {
    // Strip stray leading/trailing slashes + whitespace that can sneak in via
    // the ?id= URL param. A trailing slash produced malformed URLs like
    // ".../trainings/{id}//scene-audios" and ".../training/{id}//voice-profile"
    // → 404, which made voice-profile null and every training-scoped fetch fail.
    const cleanTrainingId =
      typeof idealStudioTrainingId === "string"
        ? idealStudioTrainingId.trim().replace(/^\/+|\/+$/g, "")
        : idealStudioTrainingId;
    window.idealStudioTrainingId = cleanTrainingId;
    window.idealStudio_editor_params.idealStudio_resource_id = cleanTrainingId;
  }
  window.ideaLibraryRootUrl = ideaLibraryRootUrl;
  window.tenantId = tenantId;
  window.enableAIChatPopup = enableAIChatPopup;

  if (userLoginInfo) {
    window.scorm_user_fullname = userLoginInfo.User?.UserName || "Anonymous";
  }

  if (enableWithAI && window.$) {
    window.$(document).ready(function () {
      // window.$(".identity").css("cursor", "pointer");
      // window.$(".identity").click(function () {
      //   document.location.href = "/home/aidealstudio/?rnd=" + Math.random().toString();
      // });
    });
  }

  if (window.initializeAIConfigurations) {
    window.initializeAIConfigurations({
      enableTextToImageOpenAi,
      enableTextTospeechOpenAi,
      enableTextTospeechMicrosoft,
      aiVoiceServiceType,
      aiVoiceName,
    });
  }

  startAssetGrantRefresh();
  startTokenRefresh();
};

export const getEnvironmentConfig = () => ({
  idealStudioTrainingId: window.idealStudioTrainingId,
  ideaLibraryRootUrl: window.ideaLibraryRootUrl,
  requestVerificationToken: getToken(),
  tenantId: window.tenantId,
  scormUserFullname: window.scorm_user_fullname,
});

// Base URL of the auth app (login + course list), set per environment.
export const getLoginBaseUrl = () => {
  const url = process.env.APP_BASE_URL;
  if (url) return url.replace(/\/+$/, "");
  return process.env.NODE_ENV === "production"
    ? "https://app.aideapro.ai"
    : "https://appdev.aideapro.ai";
};

// Returns true when a redirect was started, false when suppressed (visor or loop
// guard) — callers use it to avoid overwriting the loop-error screen.
export const redirectToAuth = () => {
  const pathname = window.location.pathname;

  // The visor is used two ways: (1) embedded in an iframe — console's course
  // preview — and (2) opened directly as a top-level page (a shared preview
  // link, or a user typing the URL in a browser tab). Redirecting an iframe
  // to a full login page is broken UX (the login page renders squeezed
  // inside the small preview frame), and redirecting an automated
  // recorder/render session (see recorderMode/renderMode in
  // public/index_visor.html) would hang a pipeline that expects no
  // interactive prompts — so both are suppressed. A genuine top-level human
  // visit has neither problem and needs the same "Authenticate" recovery as
  // the main editor; blanket-suppressing it here was stranding real users
  // (e.g. an expired/absent session on a directly-opened visor URL) with no
  // way to recover via the auth-required modal's "Authenticate" button.
  const isVisorAutomation = window.__recorderMode || window.__renderMode;
  const isVisorEmbedded = window.self !== window.top;
  if (pathname.includes("index_visor.html") && (isVisorAutomation || isVisorEmbedded)) {
    return false;
  }

  if (!registerAuthRedirect()) {
    triggerAuthRequired("loop");
    return false;
  }

  // Snapshot the work to IndexedDB before leaving; navigation waits for the
  // write (bounded) because an unload can abort a pending transaction.
  let draftSaved = Promise.resolve();
  try {
    const store = getStore();
    const trainingId = window.idealStudioTrainingId;
    if (store && trainingId) {
      const fullState = store.getState();
      draftSaved = Promise.race([
        // Lazy: keeps the draft store (editor-only) out of the visor bundle.
        import("./localDraftStore.es6").then(({ saveDraftFromState, LEGACY_DRAFT_KEY }) =>
          saveDraftFromState(trainingId, fullState, getUserId()).then((ok) => {
            if (ok) return;
            // IndexedDB unavailable: fall back to the legacy snapshot, which the
            // editor migrates into the same prompt on the next open.
            localStorage.setItem(
              LEGACY_DRAFT_KEY,
              JSON.stringify({
                state: {
                  present: {
                    ...fullState.undoGroup.present,
                    filesUploaded: fullState.filesUploaded,
                    status: fullState.status,
                    everPublished: fullState.everPublished,
                  },
                },
                timestamp: Date.now(),
                url: window.location.href,
              })
            );
          })
        ),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]).catch(() => {});
    }
  } catch (e) {
    console.warn("Failed to save work before auth redirect:", e);
  }

  const urlParams = new URLSearchParams(window.location.search);
  const idParam = urlParams.get("id");
  const templateParam = urlParams.get("template");
  const baseUrl = window.location.origin + window.location.pathname;

  const redirectParams = new URLSearchParams();
  if (idParam) {
    redirectParams.set("id", idParam);
  }
  if (templateParam) {
    redirectParams.set("template", templateParam);
  }
  const queryString = redirectParams.toString();
  const redirectBackUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
  const authUrl = `${getLoginBaseUrl()}/login?redirect=${encodeURIComponent(redirectBackUrl)}`;

  try {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
  } catch (e) {
    console.warn("Failed to remove token from localStorage:", e);
  }
  clearRefreshToken();
  delete window._requestVerificationToken;

  console.log("Redirecting to authentication:", authUrl);
  draftSaved.then(() => {
    window.location.href = authUrl;
  });
  return true;
};

// `?baseUrl=` is attacker-controllable (crafted link) and receives the session
// code / Bearer token, so only accept the configured MW API origin, this
// page's own origin, or a build-time ALLOWED_MW_ORIGINS entry. Only a
// single-slash path or an absolute http(s) URL is parsed (a bare "@evil.com" /
// "x" would resolve against our origin yet later be string-concatenated onto
// it by consumers). Returns origin + path with one trailing slash (query/hash
// dropped), or null.
export const sanitizeBaseUrl = (value) => {
  try {
    // eslint-disable-next-line no-control-regex
    if (typeof value !== "string" || /[\s\\\x00-\x1f\x7f]/.test(value)) return null;
    if (!/^\/(?![/\\])/.test(value) && !/^https?:\/\//i.test(value)) return null;
    const url = new URL(value, window.location.origin);
    if (url.username || url.password) return null;
    const allowed = [window.location.origin];
    if (process.env.MW_API_URL) allowed.push(new URL(process.env.MW_API_URL).origin);
    // Build-time comma-separated extra origins (e.g. video-export worker's MW).
    (process.env.ALLOWED_MW_ORIGINS || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => {
        try {
          allowed.push(new URL(s).origin);
        } catch (e) {
          /* ignore malformed entry */
        }
      });
    return allowed.includes(url.origin) ? url.origin + url.pathname.replace(/\/*$/, "/") : null;
  } catch (e) {
    return null;
  }
};

export const isAllowedBaseUrl = (value) => sanitizeBaseUrl(value) !== null;

const SESSION_CODE_EXCHANGE_PATH = "api/auth/session-code/exchange";

// Studio hand-off (2026-08): exchanges a one-time `?code=` for a fresh access
// token via mw POST /auth/session-code/exchange — the preferred replacement
// for receiving the raw session JWT in the URL (`?token=`, kept below as a
// legacy fallback). The code carries no session material itself, so a leaked
// URL/access-log/Referer is worthless after its 60s TTL and can only be
// redeemed once. Best-effort: any failure (network, expired/already-used
// code, non-2xx) resolves to null, and the caller falls through to the
// existing no-token behavior (401s -> AuthRequiredModal / login redirect)
// rather than a broken half-auth state.
const exchangeSessionCode = async (code, baseUrl) => {
  try {
    const base = (baseUrl || process.env.MW_API_URL || "").replace(/\/+$/, "");
    const response = await fetch(`${base}/${SESSION_CODE_EXCHANGE_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data || typeof data.access_token !== "string") return null;
    return {
      access_token: data.access_token,
      refresh_token: typeof data.refresh_token === "string" ? data.refresh_token : null,
    };
  } catch (e) {
    console.warn("Session code exchange failed:", e);
    return null;
  }
};

// Settles once the latest setEnvironmentFromUrl finished (incl. the async ?code=
// exchange). Never rejects. Callers that must not fire unauthenticated wait on it.
let environmentReady = Promise.resolve();
export const whenEnvironmentReady = () => environmentReady;

export const setEnvironmentFromUrl = () => {
  const run = loadEnvironmentFromUrl();
  environmentReady = run.catch(() => undefined);
  return run;
};

const loadEnvironmentFromUrl = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const config = {};
  let hasTokenOrCodeInUrl = false;

  if (urlParams.has("id")) {
    config.idealStudioTrainingId = urlParams.get("id");
  }

  // Resolved before the code/token branch below — exchangeSessionCode needs
  // it to reach the right mw host.
  if (urlParams.has("baseUrl")) {
    const safeBaseUrl = sanitizeBaseUrl(urlParams.get("baseUrl"));
    if (safeBaseUrl) {
      config.ideaLibraryRootUrl = safeBaseUrl;
    } else {
      console.warn("Ignoring non-allowlisted baseUrl query param");
    }
  }

  if (urlParams.has("code")) {
    // Preferred hand-off: a one-time exchange code, never the raw JWT.
    hasTokenOrCodeInUrl = true;
    const exchanged = await exchangeSessionCode(urlParams.get("code"), config.ideaLibraryRootUrl);
    if (exchanged) {
      config.requestVerificationToken = exchanged.access_token;
      setRefreshToken(exchanged.refresh_token);
    } else {
      console.warn("Session code exchange failed — falling through to existing no-token behavior");
    }
  } else if (urlParams.has("token")) {
    // LEGACY fallback: raw JWT in the URL. Kept for a half-deployed system
    // (console not yet minting codes, or an old link).
    hasTokenOrCodeInUrl = true;
    // A new hand-off replaces this tab's session; startTokenRefresh obtains
    // a refresh token that belongs to it.
    clearRefreshToken();
    const base64EncodedToken = urlParams.get("token");
    try {
      config.requestVerificationToken = atob(base64EncodedToken);
    } catch (e) {
      console.warn("Failed to decode base64 token:", e);
      config.requestVerificationToken = base64EncodedToken;
    }
  }

  if (urlParams.has("tenantId")) {
    config.tenantId = urlParams.get("tenantId");
  }

  initializeEnvironment(config);

  if (hasTokenOrCodeInUrl && window.history && window.history.replaceState) {
    try {
      // Remove only the token/code parameter, keep others
      urlParams.delete("token");
      urlParams.delete("code");

      const newSearch = urlParams.toString();
      const cleanUrl =
        window.location.pathname + (newSearch ? "?" + newSearch : "") + window.location.hash;

      window.history.replaceState({}, document.title, cleanUrl);
      console.log("Security: Token/code parameter has been removed from URL");
    } catch (e) {
      console.warn("Failed to clean token/code from URL:", e);
    }
  }

  return config;
};
